import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  TablePaginationConfig,
} from "antd";
import { useEffect, useState } from "react";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";


import { RootState } from "../../../app/store";
import PushNotificationDTO from "../../../shared/dtos/pushNotificationDTO";
import {
  pushNotificationsAsync,
  updatePushNotificationsValue,
} from "../../../features/pushNotification/pushNotificationSlice";
import {
  createPushNotification,
  deletePushNotification,
  sendPushNotification,
  updatePushNotification,
} from "../../../services/pushNotificationApi";
import ContainerLayout from "../../common/containerLayout";
import { TOPIC_TYPES } from "../../../utils/constants/topicTypes";
import OptionInterface from "../../../shared/interfaces/OptionInterface";
import PushNotificationForm from "../form";
import { PlusCircleTwoTone } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);


interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}
const { Column } = Table;

const PushNotifications = () => {
  const dispatch = useAppDispatch();
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [notification, setNotification] = useState<PushNotificationDTO | null>(
    null,
  );
  const [data, setData] = useState(new Array<PushNotificationDTO>());
  const { loading } = useAppSelector(
    (state: RootState) => state.pushNotifications,
  );
  const [form] = Form.useForm();

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {},
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    dispatch(updatePushNotificationsValue());
  }, [dispatch]);

  const fetchData = () => {
    dispatch(
      pushNotificationsAsync({
        page: tableParams.pagination?.current || 1,
        take: tableParams.pagination?.pageSize || 10,
      }),
    ).then(({ payload }: any) => {
      setData(payload.pushNotifications);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: payload.total,
        },
      });
    });

    setIgnoreFetch(true);
  };

  useEffect(() => {
    if (!ignoreFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreFetch, JSON.stringify(tableParams)]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<PushNotificationDTO>
      | SorterResult<PushNotificationDTO>[],
  ) => {
    setIgnoreFetch(false);
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      await deletePushNotification(notificationId);
      message.success("La notificacion se ha eliminado satisfactoriamente.");
    } catch (error) {
      message.error(
        "Ha ocurrido un error al intentar eliminar la notificacion.",
      );
    } finally {
      setIgnoreFetch(false);
    }
  };

  const sendNotification = async (notificationId: number) => {
    try {
      await sendPushNotification(notificationId);
      message.success("La notificacion se ha enviado satisfactoriamente.");
    } catch (error) {
      message.error("Ha ocurrido un error al intentar enviar la notificacion.");
    } finally {
      setIgnoreFetch(false);
    }
  };

  const handleUpsert = async (data?: PushNotificationDTO) => {
    if (!data) return;
    setFormLoading(true);
    const notificationId = notification?.id;

    try {
      if (notificationId)
        await updatePushNotification(notificationId, { ...data });
      else await createPushNotification({ ...data });

      message.success(
        `La notificacion se ha ${
          notificationId ? "actualizado" : "creado"
        } satisfactoriamente.`,
      );
    } catch (error) {
      message.error(
        `Ha ocurrido un error al intentar ${
          notificationId ? "actualizar" : "crear"
        } la notificacion.`,
      );
    } finally {
      setIgnoreFetch(false);
      handleCancel();
    }
  };

  const handleCancel = () => {
    setNotification(null);
    setFormLoading(false);
    setOpenModal(false);
  };

  const handleOpenModal = (notification: PushNotificationDTO) => {
    setNotification(notification);
    setOpenModal(true);
  };

  useEffect(() => {
    if (notification)
      form.setFieldsValue({
        title: notification.title,
        body: notification.body,
        topic: notification.topic,
        scheduledAt: notification.scheduledAtLocal && notification.timezone
          ? dayjs.tz(notification.scheduledAtLocal, notification.timezone).local()
          : "",

      });
    else
      form.setFieldsValue({
        title: "",
        body: "",
        topic: TOPIC_TYPES[0]?.value,
        scheduledAt: "",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  return (
    <ContainerLayout title="Notificaciones" showNotification={false}>
      <Col span={24}>
        <Button
          onClick={() => setOpenModal(true)}
          type="primary"
          icon={<PlusCircleTwoTone />}
        >
          Notificación
        </Button>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          scroll={{ y: "calc(-250px + 91vh)" }}
          style={{ minWidth: "100%" }}
          rowKey={(record: PushNotificationDTO) => Number(record.id)}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
        >
          <Column title="Titulo" dataIndex="title" width={100} />
          <Column title="Descripcion" width={100} dataIndex="body" />
          <Column
            title="Enviar a"
            width={40}
            dataIndex="topic"
            render={(topic) =>
              TOPIC_TYPES.find(
                (x: OptionInterface<string>) => x.value === topic,
              )?.label
            }
          />
          <Column
            title="Fecha de agenda"
            dataIndex="scheduledAtLocal"
            width={40}
            render={(_, record: PushNotificationDTO) => {
              return record.scheduledAtLocal && record.timezone
                ? dayjs.tz(record.scheduledAtLocal, record.timezone).local().format("DD-MM-YYYY HH:mm")
                : "No agendado";
            }}
          />

          <Column
            width={70}
            render={(record: PushNotificationDTO) => {
              return (
                <Space>
                  <Button onClick={() => sendNotification(record.id!)}>
                    Enviar notificacion
                  </Button>
                  <Button onClick={() => handleOpenModal(record)}>
                    Editar
                  </Button>
                  <Popconfirm
                    title="Estas seguro de eliminar esta notificacion?"
                    onConfirm={async () => {
                      await deleteNotification(record.id!);
                    }}
                    okText="Si"
                    cancelText="No"
                    placement="bottom"
                  >
                    <Button>Eliminar</Button>
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
        <Modal
          title={`${notification ? "Actualizar" : "Crear"} notificacion push`}
          open={openModal}
          onCancel={handleCancel}
          footer={null}
          width={600}
          forceRender
        >
          <PushNotificationForm
            form={form}
            formLoading={formLoading}
            notification={notification}
            handleUpsert={handleUpsert}
            handleCancel={handleCancel}
          />
        </Modal>
      </Col>
    </ContainerLayout>
  );
};

export default PushNotifications;
